import React from "react";
import { X, BookOpen } from "lucide-react";
import DetailDiagram from "@/components/DetailDiagram";

/**
 * InfoSheet (web) - read-only element explanation drawer.
 * Opens when the user clicks any element (or empty area) in a plan BEFORE
 * editing. Shows a labelled construction drawing + a full engineering
 * explanation. Mirror of the mobile InfoSheet so both platforms match.
 */
export default function InfoSheet({ info, extra = [], ar = true, onClose }) {
  if (!info) return null;
  return (
    <div
      data-testid="info-sheet-root"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(2,6,23,0.55)", backdropFilter: "blur(2px)",
        display: "flex", justifyContent: "flex-end",
      }}
    >
      <div
        data-testid="info-sheet-card"
        dir={ar ? "rtl" : "ltr"}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(440px, 100%)", height: "100%", background: "#fff",
          boxShadow: "-12px 0 40px rgba(0,0,0,0.35)",
          display: "flex", flexDirection: "column",
          animation: "infoSheetIn .28s cubic-bezier(.22,1,.36,1)",
        }}
      >
        <style>{`@keyframes infoSheetIn{from{transform:translateX(${ar ? "-" : ""}24px);opacity:.4}to{transform:translateX(0);opacity:1}}`}</style>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: "1px solid #E5E7EB" }}>
          <span style={{ fontSize: 24, width: 40, height: 40, display: "grid", placeItems: "center", background: "#F1F5F9", borderRadius: 12 }}>{info.icon}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 900, color: "#0F1115" }}>{ar ? info.title_ar : info.title_en}</div>
            {(info.subtitle_ar || info.subtitle_en) && (
              <div style={{ fontSize: 11.5, color: "#64748B", marginTop: 2 }}>{ar ? info.subtitle_ar : info.subtitle_en}</div>
            )}
          </div>
          <button data-testid="info-close" onClick={onClose}
            style={{ width: 36, height: 36, borderRadius: 18, background: "#F1F5F9", border: "none", cursor: "pointer", display: "grid", placeItems: "center" }}>
            <X size={18} color="#0F1115" />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 14 }}>
          <DetailDiagram kind={info.id} ar={ar} />

          {info.image_url && (
            <div style={{ marginTop: 12, borderRadius: 12, overflow: "hidden", height: 150, background: "#F1F5F9", position: "relative" }}>
              <img src={info.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={(e) => { e.currentTarget.style.display = "none"; }} />
              <span style={{ position: "absolute", top: 8, insetInlineStart: 8, background: "rgba(15,17,21,0.7)", color: "#fff", fontSize: 10, fontWeight: 800, padding: "3px 8px", borderRadius: 6 }}>
                {ar ? "صورة مرجعية" : "Reference"}
              </span>
            </div>
          )}

          {extra && extra.length > 0 && (
            <div style={{ marginTop: 12, padding: 10, background: "rgba(16,185,129,0.08)", border: "1px solid #10B981", borderRadius: 10 }}>
              <div style={{ color: "#065F46", fontSize: 11, fontWeight: 900, textTransform: "uppercase" }}>{ar ? "بيانات هذا العنصر" : "This element"}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                {extra.map((e, i) => (
                  <span key={i} style={{ display: "inline-flex", gap: 6, alignItems: "center", padding: "5px 10px", background: "#10B981", borderRadius: 6 }}>
                    <span style={{ color: "rgba(255,255,255,0.85)", fontSize: 10, fontWeight: 700 }}>{e.label}</span>
                    <span style={{ color: "#fff", fontSize: 11, fontWeight: 900 }}>{e.value}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {(info.sections || []).map((s, i) => (
            <div key={i} style={{ marginTop: 12, paddingBottom: 12, borderBottom: "1px solid #F1F5F9" }}>
              <div style={{ fontSize: 13, fontWeight: 900, color: "#0F1115", marginBottom: 5 }}>{ar ? s.ar : s.en}</div>
              <div style={{ fontSize: 13, color: "#334155", lineHeight: "20px", whiteSpace: "pre-line" }}>{ar ? s.body_ar : s.body_en}</div>
            </div>
          ))}

          {info.code_refs && info.code_refs.length > 0 && (
            <div style={{ marginTop: 14, padding: 10, background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <BookOpen size={13} color="#3B82F6" />
                <span style={{ fontSize: 12, fontWeight: 900, color: "#1E40AF" }}>{ar ? "المراجع الفنية" : "Code References"}</span>
              </div>
              {info.code_refs.map((r, i) => (<div key={i} style={{ fontSize: 11, color: "#1E3A8A", marginTop: 2, lineHeight: "16px" }}>• {r}</div>))}
            </div>
          )}

          <div style={{ marginTop: 14, padding: 10, background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, fontSize: 11, color: "#991B1B", lineHeight: "17px" }}>
            {ar
              ? "معلومات إرشادية عامة للفهم فقط، وليست اعتماداً هندسياً. تُراجَع وتُعتمَد من مهندس مرخّص وفق كود الموقع قبل التنفيذ."
              : "General guidance for understanding only - not an engineering approval. Review and stamp by a licensed engineer per local code before construction."}
          </div>
        </div>
      </div>
    </div>
  );
}
